/**
 * START A LEAGUE — the one way a season begins. D-EZ-103.
 *
 * There used to be two season starts (a "Season" mode on the race-week form,
 * and "Simulate a whole season" on the Career tab), the league was named only
 * on the Career tab ("RASCAL" until then), and the first thing a new player saw
 * was a form with a "Seed" field. Now:
 *
 *   1. NAME the league. Blank is allowed — the field shows the name it will get.
 *   2. WHO is racing: a brand-new garage (the inaugural season), or the 2025 Pro
 *      Season's real drivers and calendar (D-EZ-76).
 *   3. The LEAGUE SETTINGS: realism house rules (D-EZ-83…87, on) and the season
 *      sheet's scoring switches (D-EZ-98, off), one plain line each.
 *   4. Advanced: the replay number, the size of the garage and the field.
 *
 * Starting hands everything to the shell (`onCreate`), which makes a NEW league
 * (`newLeagueId()` + `freshShelf()`), begins season 1, and opens the season home
 * — "Play the next race" or "Simulate the rest of the season".
 */

import { useMemo, useState } from 'react';
import { DEFAULT_ROSTER_SIZE } from '../domain/field.ts';
import { MAX_STARTING_FIELD } from '../domain/raceWeek.ts';
import { SEASON_RACES } from '../domain/schedule.ts';
import { scheduleFor, SCENARIOS, type ScenarioId } from './entryMode.ts';
import RaceLogo from './RaceLogo.tsx';
import { RealismRules, ScoringRules } from './LeagueSettingsForm.tsx';
import { Cite } from './Cite.tsx';
import { parseSeed, randomSeed } from '../lib/seed.ts';
import { leagueNameOrSuggestion, suggestLeagueName } from '../lib/leagueNames.ts';
import { DEFAULT_LEAGUE_SETTINGS, type LeagueSettings } from '../lib/leagueSettings.ts';

export interface LeagueStart {
  leagueName: string;
  settings: LeagueSettings;
  seed: number;
  rosterSize: number;
  fieldSize: number;
  scenario: ScenarioId | null;
}

export interface LeagueCreateProps {
  /** A starting replay number; a fresh random one when absent. */
  initialSeed?: number;
  onCreate: (start: LeagueStart) => void;
  onBack?: () => void;
  /** The league currently on the workbench, if starting this one puts it away. */
  replacing?: string | null;
}

const ROSTER_SIZES = [53, 48, 43, 38] as const;
const FIELD_SIZES = [43, 38, 33, 28] as const;

export default function LeagueCreate({ initialSeed, onCreate, onBack, replacing = null }: LeagueCreateProps) {
  const [seed, setSeed] = useState(() => initialSeed ?? randomSeed());
  const [seedText, setSeedText] = useState(() => String(seed));
  const [name, setName] = useState('');
  const [nameRoll, setNameRoll] = useState(0);
  const [scenario, setScenario] = useState<ScenarioId | null>(null);
  const [settings, setSettings] = useState<LeagueSettings>(() => ({
    race: { ...DEFAULT_LEAGUE_SETTINGS.race },
    sheet: { ...DEFAULT_LEAGUE_SETTINGS.sheet },
  }));
  const [rosterSize, setRosterSize] = useState<number>(DEFAULT_ROSTER_SIZE);
  const [fieldSize, setFieldSize] = useState<number>(MAX_STARTING_FIELD);

  const suggestion = useMemo(() => suggestLeagueName(seed + nameRoll * 7919), [seed, nameRoll]);
  const reading = useMemo(() => parseSeed(seedText), [seedText]);
  const schedule = useMemo(() => scheduleFor(seed, scenario), [seed, scenario]);
  const opener = schedule.races[0];
  const pro = SCENARIOS.find((s) => s.id === 'pro-2025') ?? SCENARIOS[0];

  return (
    <section className="newleague" aria-labelledby="newleague-h">
      <header className="newleague__head">
        <p className="kicker">A new league</p>
        <h1 id="newleague-h">Start your league</h1>
        <p className="newleague__dek">
          Name it, pick who races, set the house rules. Then it is {SEASON_RACES} races, a playoff, a champion and a
          winter of retirements and rookies — as many seasons as you like.
        </p>
        {replacing && (
          <p className="newleague__replacing">
            <b>{replacing}</b> stays on this device (and in your account if you saved it) — load it from Account to
            go back to it.
          </p>
        )}
      </header>

      <form
        className="newleague__form"
        onSubmit={(e) => {
          e.preventDefault();
          onCreate({
            leagueName: leagueNameOrSuggestion(name, suggestion),
            settings,
            seed,
            rosterSize: scenario ? DEFAULT_ROSTER_SIZE : rosterSize,
            fieldSize,
            scenario,
          });
        }}
      >
        {/* 1 · THE NAME */}
        <div className="newleague__step">
          <label className="newleague__label" htmlFor="league-name">
            League name
          </label>
          <div className="newleague__namerow">
            <input
              id="league-name"
              className="newleague__name"
              value={name}
              maxLength={40}
              placeholder={suggestion}
              autoComplete="off"
              onChange={(e) => setName(e.target.value)}
            />
            <button type="button" className="btn btn--quiet newleague__btn" onClick={() => setNameRoll((n) => n + 1)}>
              Suggest another
            </button>
          </div>
          <p className="newleague__note">
            {name.trim() ? (
              <>Your league will be called <b>{leagueNameOrSuggestion(name, suggestion)}</b>.</>
            ) : (
              <>Leave it blank and it will be called <b>{suggestion}</b>.</>
            )}
          </p>
        </div>

        {/* 2 · WHO RACES */}
        <fieldset className="newleague__step newleague__who">
          <legend className="newleague__label">Who races</legend>
          <label className={`whocard${scenario === null ? ' is-on' : ''}`}>
            <input type="radio" name="league-who" checked={scenario === null} onChange={() => setScenario(null)} />
            <span>
              <b>A brand-new league</b>
              <span>
                A garage of drivers nobody has heard of yet, and a calendar of your own. Everyone is a rookie on
                day one.
              </span>
            </span>
          </label>
          <label className={`whocard${scenario === pro.id ? ' is-on' : ''}`}>
            <input type="radio" name="league-who" checked={scenario === pro.id} onChange={() => setScenario(pro.id)} />
            <span>
              <b>The {pro.title}</b>
              <span>
                The real drivers, the real {pro.shops} shops and the real calendar, from the {pro.opener} to the{' '}
                {pro.finale}. After the green flag, anything can happen.
              </span>
            </span>
          </label>
          {opener && (
            <p className="newleague__opener">
              <RaceLogo raceKey={opener.raceKey} size={28} />
              <span>
                Opening day: <b>{opener.raceName}</b> · {opener.trackName}
              </span>
            </p>
          )}
        </fieldset>

        {/* 3 · LEAGUE SETTINGS */}
        <div className="newleague__step">
          <p className="newleague__label">League settings</p>
          <p className="newleague__note">These carry from season to season. You can change them for any next season.</p>
          <div className="newleague__rules">
            <RealismRules value={settings.race} onChange={(race) => setSettings((s) => ({ ...s, race }))} idPrefix="nl-race" />
            <ScoringRules value={settings.sheet} onChange={(sheet) => setSettings((s) => ({ ...s, sheet }))} idPrefix="nl-sheet" />
          </div>
        </div>

        {/* 4 · ADVANCED */}
        <details className="advanced newleague__advanced">
          <summary>Advanced</summary>
          <div className="field field--row">
            <div>
              <label htmlFor="league-replay">Replay number</label>
              <input
                id="league-replay"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                spellCheck={false}
                value={seedText}
                onChange={(e) => {
                  setSeedText(e.target.value);
                  const parsed = parseSeed(e.target.value).seed;
                  if (parsed !== null) setSeed(parsed);
                }}
              />
            </div>
            <button
              type="button"
              className="btn btn--quiet newleague__btn"
              onClick={() => {
                const next = randomSeed();
                setSeed(next);
                setSeedText(String(next));
              }}
            >
              New number
            </button>
          </div>
          <p className="field__note">
            {reading.problem ? (
              <b className="warn">{reading.problem} Still using {seed}.</b>
            ) : (
              <>The same number deals the same garage, calendar and dice — type one in to play a league again.</>
            )}
            <Cite> (seed {seed})</Cite>
          </p>
          {scenario ? null : (
            <div className="field">
              <label htmlFor="league-roster">Drivers with a licence</label>
              <select id="league-roster" value={rosterSize} onChange={(e) => setRosterSize(Number(e.target.value))}>
                {ROSTER_SIZES.map((n) => (
                  <option key={n} value={n}>
                    {n} drivers{n === DEFAULT_ROSTER_SIZE ? ' · full garage' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="field">
            <label htmlFor="league-field">Cars that start each race</label>
            <select id="league-field" value={fieldSize} onChange={(e) => setFieldSize(Number(e.target.value))}>
              {FIELD_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n} cars{n === MAX_STARTING_FIELD ? ' · full field' : ''}
                </option>
              ))}
            </select>
          </div>
        </details>

        <div className="newleague__go">
          <button type="submit" className="btn btn--hot btn--big newleague__btn">
            Start the league
          </button>
          {onBack && (
            <button type="button" className="btn btn--quiet newleague__btn" onClick={onBack}>
              Back
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
